import { useNavigate } from 'react-router-dom';
import { Card, Stack, Title, Text, Group } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../../lib/types';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { CopyableId } from '../../../../components/CopyableId';
import { RunStatusBadge } from '../../components/RunStatusBadge';
import { formatDuration } from '../../lib/duration';
import { useT } from '../../../../i18n/I18nProvider';

type FlowRun = NonNullable<Awaited<ReturnType<typeof admin.listFlowRuns>>['data']>[number];

// Siblings come out of the same capped list the page already holds, so both
// tables are bounded twice over: by the fetch window and by this slice. The
// caption says so rather than presenting a partial answer as a full one.
const MAX_SIBLINGS = 50;

export function RelatedRunsTab({ run, allRuns, sinceDays }: { run: FlowRun; allRuns: FlowRun[]; sinceDays: number }) {
  const navigate = useNavigate();
  const t = useT();

  const sameFlow = allRuns.filter((r) => r.flow_id === run.flow_id && r.id !== run.id).slice(0, MAX_SIBLINGS);
  const sameConv = run.conversation_id
    ? allRuns.filter((r) => r.conversation_id === run.conversation_id && r.id !== run.id).slice(0, MAX_SIBLINGS)
    : [];

  const bound = t('flows.view.related.bound', { count: MAX_SIBLINGS, days: sinceDays });

  return (
    // Side by side, as the design shows them: the two questions ("has this
    // flow been failing?" and "what else happened in this thread?") are read
    // against each other, not one after the other.
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, alignItems: 'start' }}>
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline" wrap="nowrap">
            <Stack gap="xs">
              <Title order={4}>{t('flows.view.related.sameFlow')}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{bound}</Text>
            </Stack>
            <Text role="caption" c="var(--rr-color-text-muted)">{run.flow_name ?? t('flows.flowDeleted')}</Text>
          </Group>
          <DataTable<Indexed<FlowRun>>
            records={sameFlow as Indexed<FlowRun>[]}
            idAccessor="id"
            emptyState={t('flows.view.related.sameFlowEmpty')}
            onRowClick={(r) => navigate(`/flows/${r.id}`)}
            columns={[
              {
                accessor: 'started_at',
                title: t('flows.columns.startedAt'),
                render: (r) => <span title={formatDateTime(r.started_at)}>{formatRelative(r.started_at)}</span>
              },
              { accessor: 'status', title: t('common.status'), render: (r) => <RunStatusBadge status={r.status} /> },
              { accessor: 'contact_msisdn', title: t('flows.columns.contact'), render: (r) => r.contact_msisdn ?? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.notAvailable')}</Text> },
              {
                accessor: 'finished_at',
                title: t('flows.columns.duration'),
                render: (r) => formatDuration(r.started_at, r.finished_at)
              }
            ]}
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline" wrap="nowrap">
            <Stack gap="xs">
              <Title order={4}>{t('flows.view.related.sameConversation')}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{bound}</Text>
            </Stack>
            {run.conversation_id
              ? <CopyableId value={run.conversation_id} label={t('flows.copyConversationId')} chars={12} />
              : <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.view.related.noConversation')}</Text>}
          </Group>
          {run.conversation_id ? (
            <DataTable<Indexed<FlowRun>>
              records={sameConv as Indexed<FlowRun>[]}
              idAccessor="id"
              emptyState={t('flows.view.related.sameConversationEmpty')}
              onRowClick={(r) => navigate(`/flows/${r.id}`)}
              columns={[
                {
                  accessor: 'started_at',
                  title: t('flows.columns.startedAt'),
                  render: (r) => <span title={formatDateTime(r.started_at)}>{formatRelative(r.started_at)}</span>
                },
                { accessor: 'flow_name', title: t('flows.columns.flow'), render: (r) => r.flow_name ?? t('flows.flowDeleted') },
                { accessor: 'status', title: t('common.status'), render: (r) => <RunStatusBadge status={r.status} /> },
                {
                  accessor: 'finished_at',
                  title: t('flows.columns.duration'),
                  render: (r) => formatDuration(r.started_at, r.finished_at)
                }
              ]}
            />
          ) : (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.view.related.notLinked')}</Text>
          )}
        </Stack>
      </Card>
    </div>
  );
}
